import type { PayloadAction } from "@reduxjs/toolkit";
import { getAuth } from "firebase/auth";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import type { KeyMomentType } from "../../../types/post.types";
import {
	addPostRequest,
	blockCommentUserRequest,
	cardRequest,
	correctTimeRequest,
	createBroadcastRequest,
	deletePostRequest,
	endBroadcastRequest,
	endPollRequest,
	fullTimeRequest,
	goalRequest,
	halfTimeRequest,
	kickoffRequest,
	markCommentReadRequest,
	pollRequest,
	removeCardRequest,
	removeSubstitutionRequest,
	removeSubPairRequest,
	replyToCommentRequest,
	secondHalfRequest,
	setBroadcastId,
	setBroadcastUserId,
	setFixtureId,
	setFullTimeAt,
	setHalfTimeAt,
	setSecondHalfStartTime,
	setStartTime,
	setTimeCorrection,
	shareCommentToFeedRequest,
	substitutionRequest,
	updatePostRequest,
	varReviewRequest,
} from "../broadcast.actions";

const API_URL = process.env.REACT_APP_API_URL ?? "http://localhost:3001";

async function getAuthHeaders(): Promise<Record<string, string>> {
	const user = getAuth().currentUser;
	if (!user || user.isAnonymous) return {};
	const token = await user.getIdToken();
	return { Authorization: `Bearer ${token}` };
}

async function apiPost(path: string, body?: object) {
	const auth = await getAuthHeaders();
	const res = await fetch(`${API_URL}${path}`, {
		method: "POST",
		headers: { "Content-Type": "application/json", ...auth },
		body: body ? JSON.stringify(body) : undefined,
	});
	if (!res.ok) throw new Error(`API error: ${res.status}`);
	return res.json();
}

async function apiPatch(path: string, body: object) {
	const auth = await getAuthHeaders();
	const res = await fetch(`${API_URL}${path}`, {
		method: "PATCH",
		headers: { "Content-Type": "application/json", ...auth },
		body: JSON.stringify(body),
	});
	if (!res.ok) throw new Error(`API error: ${res.status}`);
	return res.json();
}

async function apiDelete(path: string) {
	const auth = await getAuthHeaders();
	const res = await fetch(`${API_URL}${path}`, {
		method: "DELETE",
		headers: auth,
	});
	if (!res.ok) throw new Error(`API error: ${res.status}`);
	return res.json();
}

function* handleCreateBroadcast(
	action: PayloadAction<{ homeTeam: string; awayTeam: string; homeLogo?: string; awayLogo?: string; venue?: string; location?: string; fixtureId?: number }>,
) {
	try {
		const data = (yield call(apiPost, "/api/broadcasts", action.payload)) as {
			id: string;
		};
		yield put(setBroadcastId(data.id));
		const uid = getAuth().currentUser?.uid;
		if (uid) yield put(setBroadcastUserId(uid));
		if (action.payload.fixtureId) {
			yield put(setFixtureId(action.payload.fixtureId));
		}
	} catch (err) {
		console.error("Failed to create broadcast", err);
	}
}

function* handleAddPost(
	action: PayloadAction<{ broadcastId: string; text: string; share?: boolean; hashtags?: string[]; keyMomentType?: KeyMomentType; postType?: string }>,
) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/posts`, {
			text: action.payload.text,
			share: action.payload.share ?? false,
			hashtags: action.payload.hashtags ?? [],
			keyMomentType: action.payload.keyMomentType,
			postType: action.payload.postType,
		});
	} catch (err) {
		console.error("Failed to add post", err);
	}
}

function* handleUpdatePost(
	action: PayloadAction<{ broadcastId: string; postId: string; text: string }>,
) {
	try {
		yield call(
			apiPatch,
			`/api/broadcasts/${action.payload.broadcastId}/posts/${action.payload.postId}`,
			{ text: action.payload.text },
		);
	} catch (err) {
		console.error("Failed to update post", err);
	}
}

function* handleDeletePost(
	action: PayloadAction<{ broadcastId: string; postId: string }>,
) {
	try {
		yield call(
			apiDelete,
			`/api/broadcasts/${action.payload.broadcastId}/posts/${action.payload.postId}`,
		);
	} catch (err) {
		console.error("Failed to delete post", err);
	}
}

function* handleGoal(action: PayloadAction<{ broadcastId: string; team: "home" | "away"; teamId?: number; action?: "add" | "remove"; scorerId?: number; comment?: string; share?: boolean; hashtags?: string[] }>) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/goal`, {
			team: action.payload.team,
			teamId: action.payload.teamId,
			action: action.payload.action ?? "add",
			scorerId: action.payload.scorerId,
			comment: action.payload.comment,
			share: action.payload.share,
			hashtags: action.payload.hashtags,
		});
	} catch (err) {
		console.error("Failed to record goal", err);
	}
}

function* handleKickoff(action: PayloadAction<{ broadcastId: string; offsetMinutes?: number }>) {
	try {
		const data = (yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/kickoff`,
			{ offsetMinutes: action.payload.offsetMinutes ?? 0 },
		)) as { startTime: string; halfTimeAt?: string; secondHalfStartTime?: string };
		yield put(setStartTime(data.startTime));
		if (data.halfTimeAt) yield put(setHalfTimeAt(data.halfTimeAt));
		if (data.secondHalfStartTime) yield put(setSecondHalfStartTime(data.secondHalfStartTime));
	} catch (err) {
		console.error("Failed to kick off", err);
	}
}

function* handleFullTime(action: PayloadAction<{ broadcastId: string }>) {
	try {
		const data = (yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/fulltime`,
		)) as { fullTimeAt: string };
		yield put(setFullTimeAt(data.fullTimeAt));
	} catch (err) {
		console.error("Failed to set full time", err);
	}
}

function* handleHalfTime(action: PayloadAction<{ broadcastId: string }>) {
	try {
		const data = (yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/halftime`,
		)) as { halfTimeAt: string };
		yield put(setHalfTimeAt(data.halfTimeAt));
	} catch (err) {
		console.error("Failed to set half time", err);
	}
}

function* handleEndBroadcast(action: PayloadAction<{ broadcastId: string }>) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/end`);
	} catch (err) {
		console.error("Failed to end broadcast", err);
	}
}

function* handlePoll(
	action: PayloadAction<{ broadcastId: string; question: string; options: string[] }>,
) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/polls`, {
			question: action.payload.question,
			options: action.payload.options,
		});
	} catch (err) {
		console.error("Failed to create poll", err);
	}
}

function* handleEndPoll(
	action: PayloadAction<{ broadcastId: string; pollId: string; share?: boolean; hashtags?: string[]; comment?: string }>,
) {
	try {
		yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/polls/${action.payload.pollId}/end`,
			{
				share: action.payload.share,
				hashtags: action.payload.hashtags,
				comment: action.payload.comment,
			},
		);
	} catch (err) {
		console.error("Failed to end poll", err);
	}
}

function* handleCard(
	action: PayloadAction<{ broadcastId: string; team: "home" | "away"; teamId?: number; player: string; playerId?: number; type: "yellow" | "2Yellow" | "red"; comment?: string; share?: boolean; hashtags?: string[] }>,
) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/cards`, {
			team: action.payload.team,
			teamId: action.payload.teamId,
			player: action.payload.player,
			playerId: action.payload.playerId,
			type: action.payload.type,
			comment: action.payload.comment,
			share: action.payload.share,
			hashtags: action.payload.hashtags,
		});
	} catch (err) {
		console.error("Failed to record card", err);
	}
}

function* handleRemoveCard(action: PayloadAction<{ broadcastId: string; cardId: string }>) {
	try {
		yield call(apiDelete, `/api/broadcasts/${action.payload.broadcastId}/cards/${action.payload.cardId}`);
	} catch (err) {
		console.error("Failed to remove card", err);
	}
}

function* handleRemoveSubstitution(action: PayloadAction<{ broadcastId: string; substitutionId: string }>) {
	try {
		yield call(apiDelete, `/api/broadcasts/${action.payload.broadcastId}/substitutions/${action.payload.substitutionId}`);
	} catch (err) {
		console.error("Failed to reverse substitution", err);
	}
}

function* handleRemoveSubPair(action: PayloadAction<{ broadcastId: string; substitutionId: string; pairIndex: number }>) {
	try {
		yield call(apiPatch, `/api/broadcasts/${action.payload.broadcastId}/substitutions/${action.payload.substitutionId}`, { pairIndex: action.payload.pairIndex });
	} catch (err) {
		console.error("Failed to reverse sub pair", err);
	}
}

function* handleSubstitution(
	action: PayloadAction<{ broadcastId: string; team: "home" | "away"; subs: { playerOut: string; playerIn: string }[] }>,
) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/substitutions`, {
			team: action.payload.team,
			subs: action.payload.subs,
		});
	} catch (err) {
		console.error("Failed to record substitution", err);
	}
}

function* handleVarReview(action: PayloadAction<{ broadcastId: string; active: boolean }>) {
	try {
		yield call(apiPost, `/api/broadcasts/${action.payload.broadcastId}/var-review`, {
			active: action.payload.active,
		});
	} catch (err) {
		console.error("Failed to update VAR review", err);
	}
}

function* handleSecondHalf(action: PayloadAction<{ broadcastId: string }>) {
	try {
		const data = (yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/secondhalf`,
		)) as { secondHalfStartTime: string };
		yield put(setSecondHalfStartTime(data.secondHalfStartTime));
	} catch (err) {
		console.error("Failed to start second half", err);
	}
}

function* handleReplyToComment(
	action: PayloadAction<{ broadcastId: string; commentId: string; reply: string }>,
) {
	try {
		yield call(
			apiPatch,
			`/api/broadcasts/${action.payload.broadcastId}/comments/${action.payload.commentId}/reply`,
			{ reply: action.payload.reply },
		);
	} catch (err) {
		console.error("Failed to reply to comment", err);
	}
}

function* handleShareCommentToFeed(
	action: PayloadAction<{ broadcastId: string; commentId: string }>,
) {
	try {
		yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/comments/${action.payload.commentId}/share`,
		);
	} catch (err) {
		console.error("Failed to share comment to feed", err);
	}
}

function* handleMarkCommentRead(
	action: PayloadAction<{ broadcastId: string; commentId: string; read?: boolean }>,
) {
	try {
		yield call(
			apiPatch,
			`/api/broadcasts/${action.payload.broadcastId}/comments/${action.payload.commentId}/read`,
			{ read: action.payload.read ?? true },
		);
	} catch (err) {
		console.error("Failed to mark comment as read/unread", err);
	}
}

function* handleBlockCommentUser(
	action: PayloadAction<{ broadcastId: string; commentId: string }>,
) {
	try {
		yield call(
			apiPost,
			`/api/broadcasts/${action.payload.broadcastId}/comments/${action.payload.commentId}/block`,
		);
	} catch (err) {
		console.error("Failed to block user", err);
	}
}

function* handleCorrectTime(action: PayloadAction<{ broadcastId: string; minute: number }>) {
	try {
		const data = (yield call(
			apiPatch,
			`/api/broadcasts/${action.payload.broadcastId}/correct-time`,
			{ minute: action.payload.minute },
		)) as { secondHalfStartTime?: string; timeCorrection?: { appliedAt: string; minute: number } };
		if (data.secondHalfStartTime) yield put(setSecondHalfStartTime(data.secondHalfStartTime));
		if (data.timeCorrection) yield put(setTimeCorrection(data.timeCorrection));
	} catch (err) {
		console.error("Failed to correct time", err);
	}
}

export function* broadcastWatchSaga() {
	yield takeLatest(createBroadcastRequest.type, handleCreateBroadcast);
	yield takeEvery(addPostRequest.type, handleAddPost);
	yield takeEvery(updatePostRequest.type, handleUpdatePost);
	yield takeEvery(deletePostRequest.type, handleDeletePost);
	yield takeEvery(goalRequest.type, handleGoal);
	yield takeLatest(endBroadcastRequest.type, handleEndBroadcast);
	yield takeEvery(pollRequest.type, handlePoll);
	yield takeEvery(endPollRequest.type, handleEndPoll);
	yield takeEvery(cardRequest.type, handleCard);
	yield takeEvery(removeCardRequest.type, handleRemoveCard);
	yield takeEvery(substitutionRequest.type, handleSubstitution);
	yield takeEvery(removeSubstitutionRequest.type, handleRemoveSubstitution);
	yield takeEvery(removeSubPairRequest.type, handleRemoveSubPair);
	yield takeEvery(replyToCommentRequest.type, handleReplyToComment);
	yield takeEvery(shareCommentToFeedRequest.type, handleShareCommentToFeed);
	yield takeEvery(markCommentReadRequest.type, handleMarkCommentRead);
	yield takeEvery(blockCommentUserRequest.type, handleBlockCommentUser);
	yield takeLatest(varReviewRequest.type, handleVarReview);
	yield takeLatest(kickoffRequest.type, handleKickoff);
	yield takeLatest(fullTimeRequest.type, handleFullTime);
	yield takeLatest(halfTimeRequest.type, handleHalfTime);
	yield takeLatest(secondHalfRequest.type, handleSecondHalf);
	yield takeLatest(correctTimeRequest.type, handleCorrectTime);
}
